import 'dayjs/locale/ja';

import { DatePickerProps } from 'antd';
import defaultLocale from 'antd/es/date-picker/locale/ja_JP';
import classNames from 'classnames/bind';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { FORMAT_DATE, FORMAT_DATE_PLACEHOLDER } from '~/constants/common';

import styles from './index.module.scss';
import DatePicker from '../DatePicker';

const cx = classNames.bind(styles);

type IProps = Omit<DatePickerProps, 'placeholder'> & {
  placeholder?: string[];
  value?: (Dayjs | null)[];
  onChange?: (value: (Dayjs | null)[]) => void;
  disabledDate?: [
    (currentDate: Dayjs) => boolean,
    (currentDate: Dayjs) => boolean,
  ];
  dateRender?: [
    (currentDate: Dayjs) => React.ReactNode,
    (currentDate: Dayjs) => React.ReactNode,
  ];
  disablePast?: boolean;
  disableFuture?: boolean;
  showTime?: boolean;
};

const RangePicker = (props: IProps): React.ReactElement => {
  const [values, setValues] = useState<(Dayjs | null)[]>([null, null]);
  const {
    placeholder,
    value,
    onChange,
    disabledDate,
    dateRender,
    format = FORMAT_DATE,
    locale = defaultLocale,
    showTime,
    ...commonProps
  } = props;

  const handleChange = (index, date: any): void => {
    const newValues = [...values];
    newValues[index] = date;
    setValues(newValues);
    onChange?.(newValues);
  };

  const disabledStartDate = (currentDate: Dayjs): boolean => {
    if (props.disablePast && currentDate.isBefore(dayjs().startOf('day'))) {
      return true;
    }
    if (props.disableFuture && currentDate.isAfter(dayjs().endOf('day'))) {
      return true;
    }

    return !!values[1] && currentDate.isAfter(values[1], 'day');
  };

  const disabledEndDate = (currentDate: Dayjs): boolean => {
    if (props.disablePast && currentDate.isBefore(dayjs().startOf('day'))) {
      return true;
    }
    if (props.disableFuture && currentDate.isAfter(dayjs().endOf('day'))) {
      return true;
    }

    return !!values[0] && currentDate.isBefore(values[0], 'day');
  };

  const renderStartDate = (currentDate: Dayjs): React.ReactNode => {
    const classList: any = [];

    if (values[1] && currentDate.isSame(values[1], 'day')) {
      classList.push('selected-day');
    }

    if (
      values[0] &&
      values[1] &&
      currentDate.isBefore(values[1], 'day') &&
      currentDate.isAfter(values[0], 'day')
    ) {
      classList.push('disabled-day');
    }

    return (
      <span className={cx('ant-picker-cell-inner', ...classList)}>
        {currentDate.format('DD')}
      </span>
    );
  };

  const renderEndDate = (currentDate: Dayjs): React.ReactNode => {
    const classList: any = [];

    if (values[0] && currentDate.isSame(values[0], 'day')) {
      classList.push('selected-day');
    }

    if (
      values[0] &&
      values[1] &&
      currentDate.isBefore(values[1], 'day') &&
      currentDate.isAfter(values[0], 'day')
    ) {
      classList.push('disabled-day');
    }

    return (
      <span className={cx('ant-picker-cell-inner', ...classList)}>
        {currentDate.format('DD')}
      </span>
    );
  };

  useEffect(() => {
    if (value) {
      setValues(value);
    }
  }, value);

  return (
    <div className={cx('wrapper')}>
      <DatePicker
        className={cx('datePicker')}
        value={values[0]}
        placeholder={placeholder?.[0] || FORMAT_DATE_PLACEHOLDER}
        format={format}
        onChange={(date): void => handleChange(0, date)}
        disabledDate={(disabledDate?.[0] || disabledStartDate) as any}
        dateRender={(dateRender?.[0] || renderStartDate) as any}
        showTime={showTime}
        {...commonProps}
      />
      <span>〜</span>
      <DatePicker
        value={values[1]}
        placeholder={placeholder?.[1] || FORMAT_DATE_PLACEHOLDER}
        format={format}
        onChange={(date): void => handleChange(1, date)}
        disabledDate={(disabledDate?.[1] || disabledEndDate) as any}
        dateRender={(dateRender?.[1] || renderEndDate) as any}
        showTime={showTime}
        {...commonProps}
      />
    </div>
  );
};

export default RangePicker;
